// ReportesDiariosResponsable.js
// 5.2 — Reporte diario de responsables (rol admin).
// Revisa los reportes de sus coordinadoras asignadas y envía un resumen
// interno antes de las 18:45.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/authContext";

const HORA_LIMITE = "18:45";

const AYUDA_LABEL = {
  atencion_cliente: "📞 Atención al cliente",
  asistentas: "🧹 Asistentas",
  almacen: "📦 Almacén",
  direccion: "🏢 Dirección",
};
const RUTA_LABEL = { si: "✅ Sí", parcial: "🟡 Parcial", no: "❌ No" };

const CAMPOS_COORD = [
  ["pisos_visitados", "🏠 Pisos visitados"],
  ["incidencias_nuevas", "🆕 Incidencias nuevas"],
  ["incidencias_resueltas", "✅ Incidencias resueltas"],
  ["incidencias_pendientes", "⏳ Incidencias pendientes"],
  ["asistentas_notas", "🧑‍🔧 Asistentas"],
  ["residentes_sensibles", "💗 Residentes sensibles"],
  ["familias_contactadas", "👨‍👩‍👧 Familias contactadas"],
  ["compras_realizadas", "🛒 Compras realizadas"],
  ["desviaciones_presupuesto", "💶 Desviaciones de presupuesto"],
  ["prioridad_manana", "🎯 Prioridad para mañana"],
];

const CAMPOS_RESP = [
  { key: "alertas_detectadas", label: "🚨 Alertas detectadas", ph: "Alertas surgidas de los reportes de tus coordinadoras…" },
  { key: "casos_criticos", label: "🔴 Casos críticos", ph: "Situaciones críticas que requieren seguimiento…" },
  { key: "incidencias_escalado", label: "⬆️ Incidencias que requieren escalado", ph: "Qué hay que escalar y a quién…" },
  { key: "riesgos_manana", label: "⚠️ Riesgos para mañana", ph: "Riesgos previstos para el día siguiente…" },
  { key: "decisiones_necesarias", label: "🧭 Decisiones necesarias", ph: "Decisiones que hay que tomar…" },
];

const hoyISO = () => new Date().toISOString().slice(0, 10);
const ESTADO = {
  enviado: { txt: "✔ Enviado", bg: "#dcfce7", col: "#15803d" },
  borrador: { txt: "✎ Borrador", bg: "#fef3c7", col: "#b45309" },
  pendiente: { txt: "○ Pendiente", bg: "#f1f5f9", col: "#64748b" },
};

const ReportesDiariosResponsable = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const usuario = user?.user || user || "";

  const [fecha, setFecha] = useState(hoyISO());
  const [coords, setCoords] = useState([]);
  const [abierto, setAbierto] = useState(null);
  const [form, setForm] = useState({ coordinadoras_revisadas: [] });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [enviado, setEnviado] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setMsg(null);
    try {
      const [rc, rr] = await Promise.all([
        axios.get(`/api/reportes-diarios/responsable/coordinadoras?fecha=${fecha}`),
        axios.get(`/api/reportes-diarios/responsable?fecha=${fecha}`),
      ]);
      setCoords(rc.data?.coordinadoras || []);
      const r = rr.data?.reporte;
      if (r) {
        setForm({
          ...r,
          coordinadoras_revisadas: Array.isArray(r.coordinadoras_revisadas) ? r.coordinadoras_revisadas : [],
        });
        setEnviado(!!r.enviado);
      } else {
        setForm({ coordinadoras_revisadas: [] });
        setEnviado(false);
      }
    } catch (_) {
      setMsg({ tipo: "error", texto: "No se pudieron cargar los reportes." });
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRevisada = (c) =>
    setForm((f) => {
      const cur = f.coordinadoras_revisadas || [];
      return { ...f, coordinadoras_revisadas: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] };
    });

  const guardar = async (enviar) => {
    setGuardando(true);
    setMsg(null);
    try {
      await axios.post("/api/reportes-diarios/responsable", { ...form, fecha, enviado: enviar });
      setEnviado(enviar);
      setMsg({ tipo: "ok", texto: enviar ? "✅ Resumen enviado correctamente." : "💾 Borrador guardado." });
    } catch (_) {
      setMsg({ tipo: "error", texto: "❌ Error al guardar el resumen." });
    } finally {
      setGuardando(false);
    }
  };

  const stats = useMemo(() => {
    const total = coords.length;
    const env = coords.filter((c) => c.estado === "enviado").length;
    const pend = coords.filter((c) => c.estado === "pendiente").length;
    return { total, env, pend };
  }, [coords]);

  const esHoy = fecha === hoyISO();
  const tarde = useMemo(() => {
    if (!esHoy) return false;
    const now = new Date();
    const [h, m] = HORA_LIMITE.split(":").map(Number);
    return now.getHours() > h || (now.getHours() === h && now.getMinutes() > m);
  }, [esHoy]);

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <button onClick={() => navigate(-1)} style={S.btnVolver}>← Volver</button>
        <input type="date" value={fecha} max={hoyISO()} onChange={(e) => setFecha(e.target.value)} style={S.date} />
      </div>

      <div style={S.wrap}>
        {/* Revisión de coordinadoras */}
        <div style={S.card}>
          <h1 style={S.title}>🗂️ Reportes de coordinadoras</h1>
          <div style={S.statsRow}>
            <span style={S.statPill}>👥 {stats.total} coordinadoras</span>
            <span style={{ ...S.statPill, background: "#dcfce7", color: "#15803d" }}>✔ {stats.env} enviados</span>
            <span style={{ ...S.statPill, background: "#f1f5f9", color: "#64748b" }}>○ {stats.pend} pendientes</span>
          </div>

          {cargando ? (
            <div style={S.loading}>Cargando…</div>
          ) : coords.length === 0 ? (
            <div style={S.loading}>No tienes coordinadoras asignadas.</div>
          ) : (
            coords.map((c) => {
              const est = ESTADO[c.estado] || ESTADO.pendiente;
              const open = abierto === c.coordinadora;
              const r = c.reporte;
              return (
                <div key={c.coordinadora} style={S.coordCard}>
                  <div style={S.coordHead} onClick={() => setAbierto(open ? null : c.coordinadora)}>
                    <span style={S.coordName}>{open ? "▾" : "▸"} {c.coordinadora}</span>
                    <span style={{ ...S.estBadge, background: est.bg, color: est.col }}>{est.txt}</span>
                  </div>
                  {open && (
                    <div style={S.coordBody}>
                      {!r ? (
                        <div style={S.empty}>Esta coordinadora aún no ha registrado su reporte.</div>
                      ) : (
                        <>
                          <div style={S.row}>
                            <strong>🚗 Ruta:</strong> {RUTA_LABEL[r.ruta_completada] || "—"}
                          </div>
                          {CAMPOS_COORD.map(([k, lab]) =>
                            r[k] ? (
                              <div key={k} style={S.row}>
                                <strong>{lab}:</strong> {r[k]}
                              </div>
                            ) : null
                          )}
                          {(r.necesito_ayuda || []).length > 0 && (
                            <div style={S.row}>
                              <strong>🆘 Necesita ayuda de:</strong>{" "}
                              {(r.necesito_ayuda || []).map((a) => AYUDA_LABEL[a] || a).join(", ")}
                            </div>
                          )}
                          {r.enviado_at && (
                            <div style={S.metaRow}>Enviado: {new Date(r.enviado_at).toLocaleString("es-ES")}</div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Resumen del responsable */}
        <div style={S.card}>
          <h1 style={S.title}>📝 Mi resumen — <strong>{usuario}</strong></h1>
          <div style={S.subline}>
            <span>Responsable</span>
            <span style={{ ...S.estBadge, ...(enviado ? { background: "#dcfce7", color: "#15803d" } : { background: "#fef3c7", color: "#b45309" }) }}>
              {enviado ? "✔ Enviado" : "✎ Borrador"}
            </span>
          </div>
          <div style={{ ...S.deadline, ...(tarde && !enviado ? S.deadlineLate : {}) }}>
            ⏰ Resumen interno antes de las <strong>{HORA_LIMITE}</strong>
            {tarde && !enviado && " — ¡fuera de plazo!"}
          </div>

          <div style={S.field}>
            <label style={S.label}>👀 Coordinadoras revisadas</label>
            <div style={S.chips}>
              {coords.map((c) => {
                const on = (form.coordinadoras_revisadas || []).includes(c.coordinadora);
                return (
                  <button key={c.coordinadora} onClick={() => toggleRevisada(c.coordinadora)} style={{ ...S.chip, ...(on ? S.chipOn : {}) }}>
                    {on ? "✔ " : ""}{c.coordinadora}
                  </button>
                );
              })}
            </div>
          </div>

          {CAMPOS_RESP.map((c) => (
            <div key={c.key} style={S.field}>
              <label style={S.label}>{c.label}</label>
              <textarea
                value={form[c.key] || ""}
                onChange={(e) => set(c.key, e.target.value)}
                placeholder={c.ph}
                style={S.textarea}
                rows={2}
              />
            </div>
          ))}

          {msg && (
            <div style={{ ...S.msg, ...(msg.tipo === "ok" ? S.msgOk : S.msgErr) }}>{msg.texto}</div>
          )}

          <div style={S.footer}>
            <button onClick={() => guardar(false)} disabled={guardando} style={S.btnDraft}>💾 Guardar borrador</button>
            <button onClick={() => guardar(true)} disabled={guardando} style={S.btnSend}>📤 Enviar resumen</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const S = {
  page: { minHeight: "100vh", background: "#f1f5f9", padding: "16px", fontFamily: "'Segoe UI', Tahoma, sans-serif" },
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, maxWidth: 900, margin: "0 auto 14px" },
  btnVolver: { padding: "10px 22px", borderRadius: 20, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 15 },
  date: { padding: "9px 12px", borderRadius: 12, border: "2px solid #cbd5e1", fontSize: 15, background: "#fff" },
  wrap: { maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 },
  card: { background: "#fff", borderRadius: 20, padding: "24px 26px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" },
  title: { margin: "0 0 12px", fontSize: 23, color: "#0f172a" },
  subline: { display: "flex", alignItems: "center", gap: 10, color: "#64748b", fontSize: 15, marginBottom: 12 },
  statsRow: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 },
  statPill: { padding: "6px 14px", borderRadius: 20, background: "#eef2ff", color: "#3730a3", fontWeight: 700, fontSize: 14 },
  loading: { textAlign: "center", color: "#64748b", padding: 30 },
  coordCard: { border: "1px solid #e2e8f0", borderRadius: 14, marginBottom: 10, overflow: "hidden" },
  coordHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", cursor: "pointer", background: "#f8fafc" },
  coordName: { fontWeight: 800, color: "#1e293b", fontSize: 16 },
  estBadge: { padding: "4px 12px", borderRadius: 20, fontSize: 13, fontWeight: 800 },
  coordBody: { padding: "14px 18px", borderTop: "1px solid #eef2f7" },
  empty: { color: "#94a3b8", fontStyle: "italic" },
  row: { marginBottom: 8, color: "#334155", fontSize: 15, lineHeight: 1.45 },
  metaRow: { marginTop: 8, color: "#94a3b8", fontSize: 13 },
  deadline: { background: "#eff6ff", color: "#1e40af", borderRadius: 12, padding: "10px 14px", fontSize: 14, marginBottom: 18, fontWeight: 600 },
  deadlineLate: { background: "#fef2f2", color: "#b91c1c" },
  field: { marginBottom: 16 },
  label: { display: "block", fontWeight: 700, color: "#334155", marginBottom: 7, fontSize: 15 },
  textarea: { width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 12, border: "2px solid #e2e8f0", fontSize: 15, resize: "vertical", fontFamily: "inherit" },
  chips: { display: "flex", flexWrap: "wrap", gap: 10 },
  chip: { padding: "9px 15px", borderRadius: 20, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  chipOn: { background: "#6366f1", color: "#fff", borderColor: "#6366f1" },
  msg: { padding: "12px 16px", borderRadius: 12, fontWeight: 700, marginBottom: 16, textAlign: "center" },
  msgOk: { background: "#dcfce7", color: "#15803d" },
  msgErr: { background: "#fee2e2", color: "#b91c1c" },
  footer: { display: "flex", gap: 14, justifyContent: "flex-end", flexWrap: "wrap" },
  btnDraft: { padding: "14px 24px", borderRadius: 14, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 800, fontSize: 16, cursor: "pointer" },
  btnSend: { padding: "14px 30px", borderRadius: 14, border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer", boxShadow: "0 8px 20px rgba(16,185,129,0.4)" },
};

export default ReportesDiariosResponsable;
